import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { tareasApi } from "src/services/tareasApi";
import { dbx } from "src/utils/urlBack_End";

// Límite de tamaño: 15MB
const UPLOAD_FILE_SIZE_LIMIT = 15 * 1024 * 1024;

export const useFileUploadTarea = (tareaId) => {
  const { empleado: user } = useSelector((state) => state.auth.user);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [agregarArchivoMutation] =
    tareasApi.useAgregarArchivoPortafolioTareaMutation();

  // Seleccionar archivo
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tamaño
    if (file.size > UPLOAD_FILE_SIZE_LIMIT) {
      setUploadError(
        `El archivo es demasiado grande. Máximo ${
          UPLOAD_FILE_SIZE_LIMIT / (1024 * 1024)
        }MB`,
      );
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
  }, []);

  // Remover archivo seleccionado
  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
    setUploadError(null);
  }, []);

  // Subir archivo
  const uploadFile = useCallback(
    async (portafolioId) => {
      if (!selectedFile || !portafolioId || !tareaId) {
        setUploadError("Faltan datos requeridos para la subida");
        return { success: false };
      }

      setUploadError(null);
      setUploading(true);

      try {
        // Subir a Dropbox
        await dbx.filesUpload({
          path: `/Tareas/${tareaId}/Portafolio/${selectedFile.name}`,
          contents: selectedFile,
          mode: "overwrite",
        });

        // Obtener URL compartida
        let shareUrl;
        try {
          const shareResponse = await dbx.sharingCreateSharedLinkWithSettings({
            path: `/Tareas/${tareaId}/Portafolio/${selectedFile.name}`,
          });
          shareUrl = shareResponse.result.url;
        } catch (shareError) {
          // Si ya existe el link compartido
          if (shareError.error?.error?.shared_link_already_exists) {
            shareUrl =
              shareError.error.error.shared_link_already_exists.metadata.url;
          } else {
            throw shareError;
          }
        }

        // Guardar en BD usando RTK Query
        await agregarArchivoMutation({
          refPortafolioTarea: portafolioId,
          urlArchivo: shareUrl,
          nombreArchivo: selectedFile.name,
          empleadoIngresaPortafolio: `${user.nombre} ${user.apellidos}`,
        }).unwrap();

        // Limpiar estado
        setSelectedFile(null);
        return { success: true };
      } catch (error) {
        console.error("Error uploading file:", error);
        setUploadError(
          error.data?.message ||
            "Error al subir el archivo. Intenta nuevamente.",
        );
        return {
          success: false,
          error: error.data?.message || error.message,
        };
      } finally {
        setUploading(false);
      }
    },
    [
      selectedFile,
      tareaId,
      user.nombre,
      user.apellidos,
      agregarArchivoMutation,
    ],
  );

  return {
    selectedFile,
    uploading,
    uploadError,
    handleFileSelect,
    clearSelectedFile,
    uploadFile,
  };
};
